<template>
  <div class="staffing-requirement-list">
    <div class="container my-4">
      <!-- Header with actions -->
      <div class="header-section">
        <h1>Staffing Requirements</h1>
        <div class="action-buttons">
          <router-link 
            v-if="$canAccess('StaffingRequirementAdd')"
            :to="{ name: 'StaffingRequirementAdd' }" 
            class="btn btn-primary"
          >
            <i class="fas fa-plus"></i> Add New Requirement
          </router-link>
        </div>
      </div>
      
      <!-- Filters -->
      <div class="card mb-4">
        <div class="card-body">
          <h5 class="card-title mb-3">Filters</h5>
          <div class="row">
            <div class="col-md-5">
              <div class="form-group">
                <label for="departmentFilter" class="form-label">Department</label>
                <select 
                  id="departmentFilter" 
                  v-model="filters.department_id" 
                  class="form-select"
                  @change="loadRequirements"
                >
                  <option value="">All Departments</option>
                  <option 
                    v-for="department in departments" 
                    :key="department.id" 
                    :value="department.id"
                  >
                    {{ department.name }}
                  </option>
                </select>
              </div>
            </div>
            <div class="col-md-5">
              <div class="form-group">
                <label for="shiftFilter" class="form-label">Shift</label>
                <select 
                  id="shiftFilter" 
                  v-model="filters.shift_id" 
                  class="form-select"
                  @change="loadRequirements"
                >
                  <option value="">All Shifts</option>
                  <option 
                    v-for="shift in shifts" 
                    :key="shift.id" 
                    :value="shift.id"
                  >
                    {{ shift.name }}
                  </option>
                </select>
              </div>
            </div>
            <div class="col-md-2 d-flex align-items-end">
              <button 
                class="btn btn-secondary w-100" 
                @click="resetFilters"
              >
                Reset
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Loading indicator -->
      <div v-if="loading" class="text-center my-5">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>

      <!-- Error alert -->
      <div v-if="error" class="alert alert-danger">
        {{ error }}
      </div>

      <!-- Requirements table -->
      <div v-if="!loading && requirements.length > 0" class="card">
        <div class="table-responsive">
          <table class="table table-hover">
            <thead class="table-light">
              <tr>
                <th>Department</th>
                <th>Shift</th>
                <th class="text-center">Min Nurses</th>
                <th class="text-center">Min Charge Nurses</th>
                <th class="text-center">Min Team Leads</th>
                <th>Required Specializations</th>
                <th class="text-center">Status</th>
                <th class="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="requirement in requirements" :key="`${requirement.department_id}-${requirement.shift_id}`">
                <td>{{ getDepartmentName(requirement.department_id) }}</td>
                <td>{{ getShiftName(requirement.shift_id) }}</td>
                <td class="text-center">{{ requirement.min_nurses_required }}</td>
                <td class="text-center">{{ requirement.min_charge_nurses_required }}</td>
                <td class="text-center">{{ requirement.min_team_leads_required }}</td>
                <td>
                  <span v-if="requirement.required_specializations && requirement.required_specializations.length">
                    <span 
                      v-for="(specId) in requirement.required_specializations"
                      :key="specId"
                      class="badge bg-info me-1"
                    >
                      {{ getSpecializationName(specId) }}
                    </span>
                  </span>
                  <span v-else class="text-muted">None</span>
                </td>
                <td class="text-center">
                  <span 
                    class="badge"
                    :class="requirement.is_active ? 'bg-success' : 'bg-danger'"
                  >
                    {{ requirement.is_active ? 'Active' : 'Inactive' }}
                  </span>
                </td>
                <td class="text-end">
                  <div class="btn-group">
                    <router-link 
                      :to="{ name: 'StaffingRequirementView', params: { id: requirement.id }}" 
                      class="btn btn-sm btn-info"
                      title="View Details"
                    >
                      <i class="fas fa-eye"></i>
                    </router-link>
                    <router-link 
                      v-if="$canAccess('StaffingRequirementEdit')"
                      :to="{ name: 'StaffingRequirementEdit', params: { id: requirement.id }}" 
                      class="btn btn-sm btn-primary"
                      title="Edit"
                    >
                      <i class="fas fa-edit"></i>
                    </router-link>
                    <button 
                      v-if="$canAccess('StaffingRequirementEdit')"
                      class="btn btn-sm"
                      :class="requirement.is_active ? 'btn-warning' : 'btn-success'"
                      @click="toggleStatus(requirement)"
                      :title="requirement.is_active ? 'Deactivate' : 'Activate'"
                    >
                      <i class="fas" :class="requirement.is_active ? 'fa-ban' : 'fa-check'"></i>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Empty state -->
      <div v-if="!loading && requirements.length === 0" class="card">
        <div class="card-body text-center py-5">
          <div class="mb-3">
            <i class="fas fa-clipboard-list fa-3x text-muted"></i>
          </div>
          <h4>No Staffing Requirements Found</h4>
          <p class="text-muted">
            {{ hasFilters ? 'Try changing your filters or' : 'Get started by' }} adding a new staffing requirement.
          </p>
          <router-link 
            v-if="$canAccess('StaffingRequirementAdd')"
            :to="{ name: 'StaffingRequirementAdd' }" 
            class="btn btn-primary mt-2"
          >
            <i class="fas fa-plus"></i> Add New Requirement
          </router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'StaffingRequirementList',
  data() {
    return {
      requirements: [],
      departments: [],
      shifts: [],
      specializations: [],
      filters: {
        department_id: '',
        shift_id: ''
      },
      loading: false,
      error: null
    }
  },
  computed: {
    hasFilters() {
      return this.filters.department_id || this.filters.shift_id;
    }
  },
  methods: {
    async loadRequirements() {
      this.loading = true;
      this.error = null;

      try {
        // Build query parameters from filters
        const params = new URLSearchParams();
        if (this.filters.department_id) {
          params.append('department_id', this.filters.department_id);
        }
        if (this.filters.shift_id) {
          params.append('shift_id', this.filters.shift_id);
        }

        const queryString = params.toString() ? `?${params.toString()}` : '';
        const response = await this.$api.get(`/staffing-requirements${queryString}`);
        this.requirements = response.data.data;
      } catch (error) {
        console.error('Error loading staffing_requirements requirements:', error);
        this.error = error.response?.data?.message || 'Failed to load staffing_requirements requirements';
      } finally {
        this.loading = false;
      }
    },

    async loadFilterOptions() {
      try {
        // Load departments
        const departmentsResponse = await this.$api.get('/departments');
        this.departments = departmentsResponse.data.data;

        // Load shifts
        const shiftsResponse = await this.$api.get('/shifts');
        this.shifts = shiftsResponse.data.data;

        // Load specializations
        const specializationsResponse = await this.$api.get('/specializations');
        this.specializations = specializationsResponse.data.data;
      } catch (error) {
        console.error('Error loading filter options:', error);
        this.error = 'Failed to load filter options';
      }
    },

    resetFilters() {
      this.filters = {
        department_id: '',
        shift_id: ''
      };
      this.loadRequirements();
    },

    getDepartmentName(departmentId) {
      const department = this.departments.find(d => d.id === departmentId);
      return department ? department.name : 'Unknown';
    },

    getShiftName(shiftId) {
      const shift = this.shifts.find(s => s.id === shiftId);
      return shift ? shift.name : 'Unknown';
    },

    getSpecializationName(specializationId) {
      const specialization = this.specializations.find(s => s.id === specializationId);
      return specialization ? specialization.name : 'Unknown';
    },

    // async toggleStatus(requirement) {
    //   try {
    //     const action = requirement.is_active ? 'deactivate' : 'activate';
    //     if (!confirm(`Are you sure you want to ${action} this staffing requirement?`)) {
    //       return;
    //     }
    //
    //     await this.$api.post(`/staffing-requirements/${requirement.id}/toggle-status`);
    //     this.$toast.success(`Staffing requirement ${action}d successfully`);
    //     this.loadRequirements();
    //   } catch (error) {
    //     console.error('Error toggling status:', error);
    //     this.$toast.error(error.response?.data?.message || `Failed to ${action} staffing requirement`);
    //   }
    // }
  },
  mounted() {
    this.loadFilterOptions();
    this.loadRequirements();
  }
}
</script>

<style scoped>
.staffing-requirement-list {
  padding-bottom: 2rem;
}

.header-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}

.form-label {
  font-weight: 500;
}

@media (max-width: 768px) {
  .header-section {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
  
  .action-buttons {
    width: 100%;
  }
}
</style>
